/**
 * Delegated begin_checkout / InitiateCheckout tracking.
 *
 * Replaces the inline onclick that used to sit on the Secure Checkout button in
 * snippets/cart-drawer.liquid and sections/main-cart-footer.liquid. That handler
 * interpolated every cart line item into a JavaScript string inside an HTML
 * attribute, and it was re-rendered by the Section Rendering API on every cart
 * update.
 *
 * This listener is bound once on `document`, so it survives the drawer markup
 * being replaced. The cart payload is read from the
 * <script type="application/json" data-dlux-checkout-cart> block rendered by
 * snippets/dlux-checkout-cart-json.liquid, so no product data is interpolated
 * into JavaScript source.
 *
 * Both calls are wrapped in try/catch and there is no preventDefault, so
 * tracking can never block or delay the form submit.
 *
 * ---------------------------------------------------------------------------
 * EXPRESS WALLET TRACKING, added 2026-07-30
 * ---------------------------------------------------------------------------
 * The Shop Pay / Apple Pay / Google Pay buttons in the cart drawer were
 * completely invisible to this file until now, so express checkouts read as
 * drop-off: the shopper reached the cart, never fired begin_checkout, and then
 * an order appeared. Anyone reading Clarity or GA4 saw an abandonment that
 * never happened.
 *
 * Since 2024-09-15 the wallet buttons hide their internals in a CLOSED shadow
 * root, so nothing inside them can be selected or listened to. What Shopify
 * does guarantee (see the Accelerated checkout upgrade guide) is that these
 * five custom elements stay in the light DOM and that clicks are `composed`,
 * so the event crosses the shadow boundary and `event.target` is retargeted to
 * the host element itself. Delegating on `document` and matching the host tag
 * is therefore the supported hook, and it is the ONLY one - do not go looking
 * for inner button classes, they will not be there.
 *
 * VERIFIED on the live drawer 2026-07-30 (Chrome / Windows): the container held
 * <shop-pay-wallet-button>, <shopify-paypal-button> and
 * <shopify-google-pay-button> as light-DOM children, and elementFromPoint over
 * the Shop Pay button returned the host element rather than anything internal.
 *
 * KNOWN GAP: PayPal is the exception. Its button renders real cross-origin
 * <iframe>s inside <shopify-paypal-button>, and clicks inside an iframe never
 * reach the parent document, so PayPal express checkouts still will not fire
 * begin_checkout. That is not fixable from the theme. Every other wallet is
 * covered. Do not read a PayPal-shaped hole in the funnel as drop-off.
 */
(function () {
  'use strict';

  // The drawer snippet and the cart page section both load this file. Bind once.
  if (window.__dluxCheckoutTrackingBound) return;
  window.__dluxCheckoutTrackingBound = true;

  // Tag name -> the value reported as `payment_type`. Sourced from Shopify's
  // accelerated checkout upgrade guide; Amazon Pay is listed for completeness
  // even though DLUX does not currently offer it.
  var WALLETS = {
    'shop-pay-wallet-button': 'shop_pay',
    'shopify-apple-pay-button': 'apple_pay',
    'shopify-google-pay-button': 'google_pay',
    'shopify-paypal-button': 'paypal',
    'shopify-amazon-pay-button': 'amazon_pay',
  };

  var WALLET_SELECTOR = Object.keys(WALLETS).join(',');

  // Wallet SDKs sometimes dispatch their own synthetic click alongside the real
  // one. Firing begin_checkout twice would inflate the funnel, so collapse
  // repeats of the same method inside a short window.
  var lastFire = { key: '', at: 0 };
  var DEDUPE_MS = 1500;

  /**
   * Find the cart JSON nearest to the clicked element.
   *
   * The Secure Checkout button sits inside [data-dlux-checkout-scope] next to
   * its own JSON block. The wallet container is a sibling of that wrapper, not
   * a descendant, so the scope lookup misses and we walk up the ancestors
   * instead. The drawer footer is a handful of nodes deep, so this is cheap.
   */
  function readCart(el) {
    var node = null;

    var scope = el && el.closest ? el.closest('[data-dlux-checkout-scope]') : null;
    if (scope) node = scope.querySelector('script[data-dlux-checkout-cart]');

    var parent = el && el.parentElement;
    while (!node && parent && parent !== document.documentElement) {
      node = parent.querySelector('script[data-dlux-checkout-cart]');
      parent = parent.parentElement;
    }

    // Last resort. On every page except /cart the drawer's block is the only
    // one in the document, so this is still the right cart.
    if (!node) node = document.querySelector('script[data-dlux-checkout-cart]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * @param {object} cart          parsed cart JSON
   * @param {string} checkoutMethod 'standard' or 'express'
   * @param {string} paymentType   'shop_pay', 'apple_pay', ... or '' for standard
   */
  function track(cart, checkoutMethod, paymentType) {
    var items = (cart && cart.items) || [];

    var key = checkoutMethod + ':' + paymentType;
    var now = Date.now();
    if (lastFire.key === key && now - lastFire.at < DEDUPE_MS) return;
    lastFire = { key: key, at: now };

    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var payload = {
          event: 'begin_checkout',
          ecommerce: {
            currency: cart.currency,
            value: cart.value,
            items: items.map(function (item) {
              return {
                item_id: item.item_id,
                item_name: item.item_name,
                price: item.price,
                quantity: item.quantity,
              };
            }),
          },
          // Additive only. Existing GA4 reports keep working untouched; these
          // two let you split express from standard instead of guessing.
          checkout_method: checkoutMethod,
        };
        if (paymentType) payload.payment_type = paymentType;
        window.dataLayer.push(payload);
      }
    } catch (e) {}

    try {
      if (typeof window.fbq === 'function') {
        // Meta's payload is left exactly as it was. InitiateCheckout has no
        // standard payment parameter and adding a custom one would only make
        // the existing audiences and reports harder to compare.
        window.fbq('track', 'InitiateCheckout', {
          content_ids: items.map(function (item) {
            return item.item_id;
          }),
          content_type: 'product',
          value: cart.value,
          currency: cart.currency,
          num_items: cart.num_items,
        });
      }
    } catch (e) {}

    try {
      if (typeof window.oaiq === 'function') {
        // ChatGPT Ads pixel wants integer minor units; the cart JSON is in dollars.
        window.oaiq('measure', 'checkout_started', {
          type: 'contents',
          amount: Math.round(cart.value * 100),
          currency: cart.currency,
          contents: items.map(function (item) {
            return {
              id: item.item_id,
              name: item.item_name,
              content_type: 'product',
              quantity: item.quantity,
              amount: Math.round(item.price * 100),
            };
          }),
        });
      }
    } catch (e) {}
  }

  document.addEventListener('click', function (evt) {
    try {
      var target = evt.target;
      if (!target || !target.closest) return;

      // Express wallets first. `target` has already been retargeted to the
      // light-DOM host by the time it reaches document, so closest() matches
      // the host element itself.
      var walletEl = target.closest(WALLET_SELECTOR);
      if (walletEl) {
        var tag = walletEl.tagName.toLowerCase();
        var cartForWallet = readCart(walletEl);
        if (cartForWallet) track(cartForWallet, 'express', WALLETS[tag] || 'unknown');
        return;
      }

      var btn = target.closest('[data-dlux-checkout-track]');
      if (!btn) return;
      var cart = readCart(btn);
      if (cart) track(cart, 'standard', '');
    } catch (e) {}
    // Deliberately no preventDefault: the form submits normally and the wallet
    // sheet opens normally.
  });
})();
